import { store } from '@/app/store';
import { fileStore } from '@/features/chats/model/draft/fileStore';
import { archiveChatAction } from '@/features/chats/sub-features/chat-info/utils/actionUtils';
import { selectKeepArchived } from '@/features/settings/model/selectors';
import { formatToCreatedAt } from '@/features/timeformatter';
import timeSync from '@/infrastructure/utils/timeSync';
import { DOWNLOAD_STATUS, MESSAGE_PROCESSING_STATUS_ID, UPLOAD_STATUS } from '@/shared/constants';
import { getLocalUserId, getMessageSenderId, getUserIdFromJid, logger } from '@/shared/utils';
import { v4 as uuidv4 } from 'uuid';
import { getChatObject, selectArchivedChatIds } from '../model';
import { getMessageFromStore } from '../model/conversation/selectors';
import { parseHtmlMessage } from './htmlParser';
import { normalizeJid } from './jidIdFormat';

export const sortNewestFirst = msgs => [...msgs].sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));

export const getChatType = jid => (jid.includes('mix.') ? 'groupchat' : 'chat');

const isForwardGroupable = msg => {
   const msgType = msg.msgType || msg.msgBody?.message_type;
   return (msgType === 'image' || msgType === 'video') && !msg.msgBody?.media?.caption;
};

/**
 * Builds a Map<msgId, newMediaGroupId> based on consecutive runs of
 * groupable media in the selected messages.
 * Breakers: any non-image/video message, or a media message with a caption.
 * A run of 1 gets '' (no group).
 */
const generateMediaGroupMap = selectedMessages => {
   const msgGroupMap = new Map();
   let currentGroup = [];

   const flushGroup = () => {
      const newGroupId = currentGroup.length > 1 ? uuidv4() : '';
      currentGroup.forEach(msgId => msgGroupMap.set(msgId, newGroupId));
      currentGroup = [];
   };

   selectedMessages.forEach(msg => {
      if (isForwardGroupable(msg)) {
         currentGroup.push(msg.msgId);
      } else {
         flushGroup();
         msgGroupMap.set(msg.msgId, '');
      }
   });

   flushGroup();

   return msgGroupMap;
};

const sanitizeForwardMsgBody = (msgBody = {}, msgId = '', msgGroupMap = new Map()) => {
   const result = { ...msgBody };

   result.replyTo = '';

   if (result.media) {
      result.media = {
         ...result.media,
         is_uploading: 0,
         mediaGroupId: msgGroupMap.get(msgId) ?? '',
      };
   }

   return result;
};

export const createBaseMessage = ({
   msgId,
   msgBody,
   msgType,
   msgStatus,
   timestamp,
   fromUserId,
   fromUserJid,
   publisherId,
   metaData = {},
   topicId = '',
   editMessageId,
   editedStatus = 0,
   userJid,
   extra = {},
}) => ({
   chatType: getChatType(fromUserJid),
   createdAt: formatToCreatedAt(timestamp),
   deleteByStatus: 0,
   deleteStatus: 0,
   fromUserId,
   publisherId,
   fromUserJid,
   metaData,
   editMessageId: editMessageId ?? msgId,
   editedStatus,
   favouriteBy: '0',
   favouriteStatus: 0,
   msgBody,
   msgId,
   msgStatus,
   msgType,
   timestamp,
   topicId,
   deliveryAndReadInfo: [],
   userJid: userJid ?? fromUserJid,
   ...extra,
});

const buildMediaBody = ({ fileId, activeChatId, messageType, replyTo = '', mediaGroupId }) => {
   const file = fileStore.getFile(fileId);
   if (!file) {
      logger.error('File not found in fileStore:', fileId);
      return null;
   }

   const draft = store.getState().draftData.drafts[activeChatId] || {};
   const metadata = draft.fileMetadata?.[fileId] || {};
   const { message: parsedCaption, mentionUserIds } = parseHtmlMessage(metadata.caption || '');

   return {
      media: {
         fileName: metadata.name || file.name || '',
         duration: metadata.duration || 0,
         file_size: file.size,
         caption: parsedCaption,
         webWidth: metadata.webWidth ?? 0,
         webHeight: metadata.webHeight ?? 0,
         androidWidth: metadata.androidWidth ?? 0,
         androidHeight: metadata.androidHeight ?? 0,
         originalWidth: metadata.originalWidth ?? 0,
         originalHeight: metadata.originalHeight ?? 0,
         thumb_image: metadata.thumbImage ?? '',
         is_uploading: 0,
         is_downloaded: 2,
         isLargeFile: true,
         blobUrl: metadata.blobUrl ?? '',
         audioType: metadata.audioType ?? '',
         mediaGroupId: parsedCaption ? '' : mediaGroupId,
      },
      mentionedUsersIds: mentionUserIds || [],
      message_type: messageType,
      nickName: '',
      replyTo,
   };
};

export const formatConversationMessage = ({ activeChatJid }) => {
   const currentUser = getLocalUserId();
   const activeChatId = getUserIdFromJid(activeChatJid);
   const draft = store.getState().draftData.drafts[activeChatId] || {};
   const fileIds = draft.fileIds || [];
   const { editMessageId, message: draftMessage, replyTo } = draft;

   if (editMessageId) {
      const originalMessage = getMessageFromStore(activeChatId, editMessageId);

      const { message: parsedContent, mentionUserIds } = parseHtmlMessage(draftMessage || '');

      // Do not allow saving an edit with purely empty content
      if (!parsedContent.trim()) return [];

      const msgBody = {
         mentionedUsersIds: mentionUserIds || [],
      };

      if (originalMessage?.msgBody?.media) {
         msgBody.media = {
            ...originalMessage.msgBody.media,
            caption: parsedContent,
         };
      } else {
         msgBody.message = parsedContent;
      }

      return [
         {
            editMessageId: uuidv4(),
            msgId: editMessageId,
            msgBody,
            editedStatus: 1,
            deliveryAndReadInfo: [
               {
                  read: [],
                  delivered: [],
               },
            ],
         },
      ];
   }

   const messages = [];
   const sharedFields = {
      fromUserId: currentUser,
      publisherId: currentUser,
      fromUserJid: activeChatJid,
      msgStatus: MESSAGE_PROCESSING_STATUS_ID,
   };

   if (draftMessage && fileIds.length === 0) {
      const { message: parsedText, mentionUserIds } = parseHtmlMessage(draftMessage);

      // Return empty array if the parsed text is blank — e.g. draft was '<br>' or whitespace only
      if (!parsedText.trim()) return [];

      const timestamp = timeSync.getSyncedTime();
      const msgId = uuidv4();

      messages.push(
         createBaseMessage({
            ...sharedFields,
            msgId,
            timestamp,
            msgType: 'text',
            msgBody: {
               mentionedUsersIds: mentionUserIds || [],
               message: parsedText,
               message_type: 'text',
               nickName: '',
               replyTo: replyTo ?? '',
            },
            editMessageId: msgId,
         }),
      );

      return messages;
   }

   // Grouping logic: consecutive media without captions form a group.
   // Messages with captions stand alone and break the sequence.
   const groups = [];
   let currentGroup = [];
   fileIds.forEach(fileId => {
      const metadata = draft.fileMetadata?.[fileId];
      const { message: parsedCaption } = parseHtmlMessage(metadata?.caption || '');
      if (parsedCaption) {
         if (currentGroup.length > 0) groups.push(currentGroup);
         groups.push([fileId]);
         currentGroup = [];
      } else {
         currentGroup.push(fileId);
      }
   });
   if (currentGroup.length > 0) groups.push(currentGroup);

   const fileGroupIdMap = new Map();
   groups.forEach(group => {
      const groupId = group.length > 1 ? uuidv4() : '';
      group.forEach(fid => fileGroupIdMap.set(fid, groupId));
   });

   fileIds.forEach((fileId, index) => {
      const metadata = draft.fileMetadata?.[fileId];
      if (!metadata) {
         logger.error('Metadata not found for fileId:', fileId);
         return;
      }

      const msgBody = buildMediaBody({
         fileId,
         activeChatId,
         messageType: metadata.msgType,
         replyTo: draft.replyTo,
         mediaGroupId: fileGroupIdMap.get(fileId) || '',
      });
      if (!msgBody) {
         logger.error('Failed to construct message body for fileId:', fileId);
         return;
      }

      const timestamp = timeSync.getSyncedTime() + index;

      messages.push(
         createBaseMessage({
            ...sharedFields,
            msgId: fileId,
            timestamp,
            msgType: metadata.msgType,
            msgBody,
            editMessageId: fileId,
         }),
      );
   });

   return sortNewestFirst(messages);
};

export const buildMeetBody = ({ title, link, scheduledDateTime, endTime }) => {
   return {
      message_type: 'meet',
      meet: {
         title,
         link,
         scheduledDateTime,
         endTime,
      },
   };
};

export const formatMeetMessage = ({ activeChatJid, title, link, scheduledDateTime, endTime, chatType }) => {
   const currentUser = getLocalUserId();
   const timestamp = timeSync.getSyncedTime();
   const msgId = uuidv4();

   const msgBody = buildMeetBody({ title, link, scheduledDateTime, endTime });

   return [
      createBaseMessage({
         fromUserId: currentUser,
         publisherId: currentUser,
         fromUserJid: activeChatJid,
         chatType,
         msgStatus: MESSAGE_PROCESSING_STATUS_ID,
         msgId,
         timestamp,
         msgType: 'meet',
         msgBody,
         editMessageId: msgId,
      }),
   ];
};

export const formatReceivedMessage = receivedMsg => {
   const formattedMessage = createBaseMessage({
      msgId: receivedMsg.msgId,
      msgBody: { ...receivedMsg.msgBody },
      msgType: receivedMsg.msgBody.message_type || 'text',
      msgStatus: receivedMsg.msgStatus,
      timestamp: receivedMsg.timestamp,
      fromUserId: receivedMsg.fromUserId,
      fromUserJid: receivedMsg.fromUserJid,
      publisherId: receivedMsg.publisherId,
      metaData: receivedMsg.metaData || {},
      topicId: receivedMsg.topicId || '',
      editMessageId: receivedMsg.editMessageId || receivedMsg.msgId,
      editedStatus: receivedMsg.editedStatus || 0,
      userJid: receivedMsg.fromUserJid,
      extra: {
         toUserId: receivedMsg.toUserId,
         toUserJid: receivedMsg.toUserJid,
         publisherJid: receivedMsg.publisherJid,
         nickName: receivedMsg.nickName || receivedMsg.msgBody.nickName,
      },
   });

   // Ensure media messages have correct upload/download status
   if (formattedMessage.msgBody?.media) {
      formattedMessage.msgBody.media = {
         ...formattedMessage.msgBody.media,
         is_uploading: formattedMessage.msgBody.media.is_uploading ?? UPLOAD_STATUS.COMPLETED,
         is_downloaded: formattedMessage.msgBody.media.is_downloaded ?? DOWNLOAD_STATUS.NOT_STARTED,
      };
   }

   return formattedMessage;
};

export const formatForwardMessages = ({ selectedMessages, targetJids }) => {
   const currentUser = getLocalUserId();
   const baseTimestamp = timeSync.getSyncedTime();

   const newMsgIds = [];
   const newMediaGroupIds = [];
   const messagesByTarget = {};

   const msgGroupMap = generateMediaGroupMap(selectedMessages);

   // Process in chronological selection order (oldest first) so generated msgIds
   // and timestamps follow the user's visible selection sequence 1 → N.
   // getSelectedMessages returns newest-first, which would otherwise emit msgIds
   // in reverse order and tie-break unpredictably in the buffer flush sort.
   const orderedSelections = [...selectedMessages].sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
   const stride = targetJids.length || 1;

   orderedSelections.forEach((message, j) => {
      const newGroupId = msgGroupMap.get(message.msgId) ?? '';

      targetJids.forEach((targetJid, i) => {
         const newMsgId = uuidv4();
         newMsgIds.push(newMsgId);
         newMediaGroupIds.push(newGroupId);

         const forwardMsg = {
            ...createBaseMessage({
               ...message,
               msgBody: sanitizeForwardMsgBody(message.msgBody, message.msgId, msgGroupMap),
               msgId: newMsgId,
               editMessageId: newMsgId,
               editedStatus: 0,
               msgStatus: MESSAGE_PROCESSING_STATUS_ID,
               timestamp: baseTimestamp + j * stride + i,
               fromUserId: currentUser,
               publisherId: currentUser,
               fromUserJid: targetJid,
               userJid: targetJid,
            }),
            isSelected: false,
            type: 'message',
         };

         if (!messagesByTarget[targetJid]) {
            messagesByTarget[targetJid] = [];
         }

         messagesByTarget[targetJid].push(forwardMsg);
      });
   });

   Object.keys(messagesByTarget).forEach(jid => {
      messagesByTarget[jid] = sortNewestFirst(messagesByTarget[jid]);
   });

   return { newMsgIds, newMediaGroupIds, messagesByTarget };
};

/**
 * Re-format unsent messages (captured on reconnect) so they land at the bottom
 * of the conversation with a fresh timestamp, while keeping their original
 * msgId (so the SDK dedups the resend) and mediaGroupId (so dispatchMessages-
 * ToConversation regroups intended media batches automatically).
 *
 * Stamping is `base + i` in chronological order, preserving the original
 * 1→N order. Note: originally-ungrouped single media from the same sender may
 * merge into one time-window group because the new stamps are ~1ms apart;
 * batches with a real mediaGroupId are unaffected.
 *
 * @param {Object} params
 * @param {Array} params.messages - Unsent leaf messages (any order).
 * @returns {Array} Re-stamped messages, newest-first.
 */
export const formatUnsentMessages = ({ messages }) => {
   const base = timeSync.getSyncedTime();
   const ordered = [...messages].sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

   const reformatted = ordered.map((msg, i) => {
      const timestamp = base + i;
      return {
         ...msg,
         timestamp,
         createdAt: formatToCreatedAt(timestamp),
         msgStatus: MESSAGE_PROCESSING_STATUS_ID,
         type: 'message',
         isSelected: false,
         isHighlighted: false,
      };
   });

   return sortNewestFirst(reformatted);
};

export const formatRecentChat = (receivedMsg, config = {}) => {
   const fromUserJid = normalizeJid(receivedMsg.fromUserJid);
   const currentUser = getLocalUserId();

   const createdAt = formatToCreatedAt(receivedMsg.timestamp);
   const updatedAt = formatToCreatedAt(receivedMsg.timestamp) || timeSync.getSyncedTime();

   const state = store.getState();

   // Chat ID is the other person (or group)
   const chatId = getUserIdFromJid(fromUserJid);

   const existingChat = getChatObject(chatId);
   const keepArchived = selectKeepArchived(state);
   const archivedChatIds = selectArchivedChatIds(state);
   if (!keepArchived && existingChat?.archiveStatus === 1 && config.changeArchiveStatus) {
      archiveChatAction({ jid: fromUserJid, status: 1, toast: false });
   }
   const isGroupProfileUpdate = receivedMsg.msgType === 'groupProfileUpdated';

   const isIncomingMessage = receivedMsg.publisherId !== currentUser;

   // Resolve unreadMessageId with three-level priority
   let unreadMessageId = '';
   if (isIncomingMessage) {
      const conversationMessages = state.conversationData?.data?.[chatId]?.messages || [];
      if (conversationMessages.length === 0) {
         const firstUnread = conversationMessages.find(
            msg => msg.type !== 'date-label' && getMessageSenderId(msg) !== currentUser,
         );
         unreadMessageId = firstUnread?.msgId;
      }
      if (existingChat?.unreadMessageId === null) {
         unreadMessageId = existingChat.msgId;
      }
      if (existingChat?.unreadMessageId) {
         unreadMessageId = existingChat?.unreadMessageId;
      }
   }

   const unreadCount = isIncomingMessage && !isGroupProfileUpdate ? (existingChat?.unreadCount || 0) + 1 : 0;
   let msgType = receivedMsg.msgBody?.message_type;
   if (isGroupProfileUpdate) {
      msgType = receivedMsg.msgType;
   } else if (!msgType) {
      msgType = existingChat?.msgId === receivedMsg.msgId ? existingChat.msgType : 'text';
   }
   const userId = chatId;

   const formatedReceivedMsg = {
      // Preserve all existing chat state first
      ...existingChat,

      // Overlay with new message data
      chatType: receivedMsg.chatType,
      archiveStatus: archivedChatIds.includes(chatId) ? 1 : 0,
      createdAt: existingChat?.createdAt || createdAt,
      deleteStatus: 0,
      fromUserId: chatId,
      isLastMessageSentByMe: !isIncomingMessage,
      metaData: receivedMsg.metaData || {},
      msgBody:
         existingChat?.msgId === receivedMsg.msgId
            ? { ...existingChat.msgBody, ...receivedMsg.msgBody }
            : receivedMsg.msgBody,
      msgId: receivedMsg.msgId,
      msgStatus: receivedMsg.msgStatus,
      msgType,
      muteStatus: existingChat?.muteStatus ?? 0,
      publisherId: receivedMsg.publisherId,
      timestamp: receivedMsg.timestamp,
      toUserId: currentUser,
      topicId: receivedMsg.topicId || '',
      unreadCount,
      updatedAt,
      userId,
      messageTime: receivedMsg.timestamp * 1000,
      unreadMessageId,
      epochTimestamp: receivedMsg.timestamp * 1000,

      ...(isGroupProfileUpdate && { profileUpdatedStatus: receivedMsg.profileUpdatedStatus }),
   };
   return formatedReceivedMsg;
};
